// 0.36.269 — Fetched + cached country flag bundle from the new
// iso_country_flags endpoint. Now the only source of admin-style
// flag overrides — Assets-based `flag-{code}` uploads went away
// in 0.36.295 along with the Assets page. If a future ship needs
// per-flag admin editing, the right surface is the iso_country_flags
// library, not a generic asset slot.
import { useCountryFlag, LANGUAGE_TO_COUNTRY } from '../services/countryFlags';

/**
 * Renders a round flag for a code that's EITHER a country alpha-2
 * (`am`, `gb`, `ru`) OR a platform language code (`hy`, `ru`, `en`).
 *
 * Priority chain (post-0.36.295):
 *   1. circle-flags SVG from iso_country_flags (lookup uses
 *      LANGUAGE_TO_COUNTRY map first, then the code as-is — so
 *      `hy` maps to `am`, but `am` itself works directly for the
 *      phone picker).
 *   2. Built-in SVG flag for hy / ru / en (kept as a resilience
 *      fallback in case the bundle fetch hasn't completed yet —
 *      ensures the language switcher is never flag-less on first
 *      paint).
 *   3. Generic globe icon.
 */
export default function FlagIcon({ code, size = 24 }) {
  const clipId = `flag-clip-${code}`;
  // 0.36.269 — Map a language code to a country code first; fall
  // back to the code itself for country-code consumers (e.g. the
  // phone picker, which passes `am`, `gb`, etc. directly).
  const countryCode = LANGUAGE_TO_COUNTRY[code] || code;
  const circleSvg = useCountryFlag(countryCode);

  // 1. circle-flags SVG from iso_country_flags. Loaded once per
  //    session via the countryFlags service; null until fetched.
  //    The service has already processed each SVG (namespaced its
  //    internal IDs to avoid mask-collision when many flags are
  //    inline on the same page, stripped upstream width/height so
  //    the wrapper controls size). We just inject it and let the
  //    wrapper's CSS size it via the preserved viewBox.
  //    Content is trustworthy — comes from our seeded library, not
  //    user input.
  if (circleSvg) {
    return (
      <span
        style={{
          display: 'inline-flex', width: size, height: size,
          flexShrink: 0, verticalAlign: 'middle',
          // The SVG inherits its rendered size from the wrapper via
          // `width: 100%; height: 100%` set inline below.
        }}
        aria-hidden="true"
        dangerouslySetInnerHTML={{
          __html: circleSvg.replace(
            /<svg\b/,
            '<svg width="100%" height="100%" style="display:block"',
          ),
        }}
      />
    );
  }

  // 3. Built-in SVG flags (kept as a fast-path / fallback before
  //    the circle-flags bundle has loaded).
  if (code === 'hy') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <clipPath id={clipId}><circle cx="12" cy="12" r="11.5" /></clipPath>
        <g clipPath={`url(#${clipId})`}>
          <rect x="0" y="0" width="24" height="8" fill="#D90012" />
          <rect x="0" y="8" width="24" height="8" fill="#0033A0" />
          <rect x="0" y="16" width="24" height="8" fill="#F2A800" />
        </g>
        <circle cx="12" cy="12" r="11.5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="0.5" />
      </svg>
    );
  }

  if (code === 'ru') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <clipPath id={clipId}><circle cx="12" cy="12" r="11.5" /></clipPath>
        <g clipPath={`url(#${clipId})`}>
          <rect x="0" y="0" width="24" height="8" fill="#FFFFFF" />
          <rect x="0" y="8" width="24" height="8" fill="#0039A6" />
          <rect x="0" y="16" width="24" height="8" fill="#D52B1E" />
        </g>
        <circle cx="12" cy="12" r="11.5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="0.5" />
      </svg>
    );
  }

  if (code === 'en') {
    return (
      <svg width={size} height={size} viewBox="0 0 60 60" aria-hidden="true">
        <clipPath id={clipId}><circle cx="30" cy="30" r="29.5" /></clipPath>
        <g clipPath={`url(#${clipId})`}>
          <rect x="0" y="0" width="60" height="60" fill="#012169" />
          <path d="M0,0 L60,60 M60,0 L0,60" stroke="white" strokeWidth="14" />
          <path d="M0,0 L60,60" stroke="#C8102E" strokeWidth="6" transform="translate(3,-3)" />
          <path d="M0,0 L60,60" stroke="#C8102E" strokeWidth="6" transform="translate(-3,3)" />
          <path d="M60,0 L0,60" stroke="#C8102E" strokeWidth="6" transform="translate(3,3)" />
          <path d="M60,0 L0,60" stroke="#C8102E" strokeWidth="6" transform="translate(-3,-3)" />
          <rect x="0" y="22" width="60" height="16" fill="white" />
          <rect x="22" y="0" width="16" height="60" fill="white" />
          <rect x="0" y="25" width="60" height="10" fill="#C8102E" />
          <rect x="25" y="0" width="10" height="60" fill="#C8102E" />
        </g>
        <circle cx="30" cy="30" r="29.5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
      </svg>
    );
  }

  // 4. Generic globe fallback
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#5a7aa2" />
      <ellipse cx="12" cy="12" rx="11" ry="4" fill="none" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="12" y1="1" x2="12" y2="23" stroke="white" strokeWidth="1" opacity="0.6" />
      <path d="M3,8 Q12,11 21,8" fill="none" stroke="white" strokeWidth="1" opacity="0.6" />
      <path d="M3,16 Q12,13 21,16" fill="none" stroke="white" strokeWidth="1" opacity="0.6" />
      <circle cx="12" cy="12" r="11" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="0.5" />
    </svg>
  );
}
